import dynamic from "next/dynamic";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material";

import HeroSection from "../../../common/components/layout/hero-section/HeroSection";
import Heading from "../../../common/components/layout/common/heading/Heading";
import toTitleCase from "../../../common/utils/utils";
import ReadingIcon from "./icons/ReadingIcon";
import QuizIcon from "./icons/QuizIcon";
import LevelIcon from "./icons/LevelIcon";
import GlobeIcon from "./icons/GlobeIcon";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import { neutral } from "../../../common/config/colors";
import authService from "../../../common/service/authService";
// import WatchLaterOutlinedIcon from "@mui/icons-material/WatchLaterOutlined";

const SnackbarRefProvider = dynamic(
  () => import("../../../common/components/notification/SnackbarRefProvider"),
  {
    ssr: false,
  }
);
const HandleRightSection = dynamic(() => import("./HandleRightSection"));

const HeadSection: React.FC<NSCourses.IHeadSectionProps> = ({ course }) => {
  const theme = useTheme();

  return (
    <HeroSection
      boxProps={{
        sx: {
          py: { xs: 12, md: 24 },
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateAreas: `". form"
                  "title form"
                  "description form"
                  "chips form"
                  ". form"`,
            [theme.breakpoints.down("md")]: {
              gridTemplateColumns: "1fr",
              gridTemplateAreas: `
                  "title"
                  "description"
                  "chips"
                  "form"
                `,
            },
          }}
        >
          <Box
            sx={{
              gridArea: "title",
              display: { md: "flex" },
              alignItems: "center",
              ml: { md: 12.5 },
            }}
          >
            <Heading
              content={course.name}
              headingProps={{
                sx: {
                  textAlign: { xs: "center", md: "left" },
                  "&::before": {
                    content: `""`,
                    position: "absolute",
                    display: { xs: "none", md: "block" },
                    height: "100%",
                    width: 10,
                    background: theme.palette.secondary.main,
                    left: -50,
                  },
                },
              }}
            />
          </Box>
          <Box
            sx={{
              gridArea: "description",
              [theme.breakpoints.down("md")]: {
                display: "grid",
                placeItems: "center",
              },
            }}
          >
            <Typography
              sx={{
                color: neutral["A500"],
                width: { xs: 277, sm: "auto" },
                fontSize: { xs: 16, sm: 18 },
                fontWeight: 500,
                ml: { md: 12.5 },
                mb: { xs: authService.isAuthenticated ? 0 : 6, md: 0 },
                [theme.breakpoints.down("md")]: {
                  textAlign: "center",
                },
              }}
            >
              {course.caption}
            </Typography>
          </Box>
          <Box
            pt={{ xs: 5, md: 9 }}
            pb={{ xs: 9, md: 6 }}
            display="flex"
            flexWrap="wrap"
            gap={4}
            fontSize={theme.spacing(3)}
            sx={{
              gridArea: "chips",
              ml: { md: 12.5 },
              [theme.breakpoints.down("md")]: {
                textAlign: "center",
                px: 4,
                justifyContent: authService.isAuthenticated
                  ? "center"
                  : "flex-start",
              },
            }}
          >
            <Chip
              label={`${course.topic_type_count.content} Tutorials`}
              icon={<ReadingIcon fontSize="inherit" />}
              sx={{
                pl: 2,
                backgroundColor: neutral["A500"],
                color: neutral["900"],
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${course.topic_type_count.exercise} Exercises`}
              icon={<QuizIcon fontSize="inherit" />}
              sx={{
                pl: 2,
                backgroundColor: neutral["A500"],
                color: neutral["900"],
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${toTitleCase(course.level)} Level`}
              icon={<LevelIcon fontSize="inherit" />}
              sx={{
                pl: 2,
                backgroundColor: neutral["A500"],
                color: neutral["900"],
                fontWeight: 600,
              }}
            />
            <Chip
              label="100% Online"
              icon={<GlobeIcon fontSize="inherit" />}
              sx={{
                pl: 2,
                backgroundColor: neutral["A500"],
                color: neutral["900"],
                fontWeight: 600,
              }}
            />
            <Chip
              label="Self-paced"
              icon={<AccessAlarmIcon fontSize="inherit" />}
              sx={{
                pl: 2,
                backgroundColor: neutral["A500"],
                color: neutral["900"],
                fontWeight: 600,
              }}
            />
          </Box>
          <Box
            sx={{
              gridArea: "form",
              display: "flex",
              justifyContent: {
                xs: "center",
                md: "flex-end",
              },
              alignItems: "center",
            }}
          >
            <SnackbarRefProvider>
              <HandleRightSection course={course} />
            </SnackbarRefProvider>
          </Box>
        </Box>
      </Box>
    </HeroSection>
  );
};

export default HeadSection;
